/* ════════════════════════════════════════════════════════════════
   Arcade coin toss — "here, you earned these."

   A handful of arcade coins is tossed onto the ice in front of you: they drop in from the far side,
   tumble end over end, clink, take a couple of smaller hops, slide a touch (it's ice), rock on their
   rims for a moment and lie there catching the light. Seen from slightly above.

   The coin is the SAME St. Beer / raccoon-tail coin the arcade uses (js/arcade-coin.js — load it first).
   Movement stays on the original art grid; four sprite pixels per grid cell preserve the engraved faces.

   window.ArcadeCoinToss.mount(container, opts) → toss
     opts.reduced        no motion: coins simply appear at rest
     opts.seed           fixed seed (repeatable tosses for QA)
     opts.sound          hooks from ArcadeCoinToss.createSynth(...) — or nothing for a silent toss
     opts.effects        false = plain coins: no sparks / rings on impact, no dust in the light
     opts.density        about how many art pixels wide the scene is (default 88; more = smaller coins, more ice)
     opts.inset          keep resting coins this fraction of the width clear of the sides (a stage whose edges fade out)
     opts.onLand(i, n)   coin i touched down for the first time (n = how many have so far)
     opts.onSettle()     every coin is at rest
   toss.toss(count, { instant, keep })   throw `count` coins. Clears the ice first — unless keep:true, which throws them in
                                         on top of the coins already lying there (the arcade landing page: your wallet, then what just landed)
   toss.advance(dt)                step + draw one frame (the caller owns the animation loop)
   toss.finish()                   skip to the settled picture
   toss.busy                       coins still moving
   toss.resize() / toss.destroy()

   ArcadeCoinToss.createSynth(getContext, getOutput, isOpen) → sound hooks (WebAudio, all synthesised)
   ════════════════════════════════════════════════════════════════ */
(function () {
    'use strict';

    var TAU = Math.PI * 2;
    var VIEW = 58 * Math.PI / 180;            // camera elevation: a coin lying flat shows as an ellipse sin(VIEW) ≈ 0.85 tall
    var SIZE = 16, THICK = 3, DETAIL = 4;      // physical size unchanged; four sprite pixels per motion pixel
    var G = 900;                              // gravity, art px / s²
    var STEP = 1 / 120;                       // fixed physics step — same motion at any frame rate
    var LAST_HOP = 62;                        // a rebound slower than this (px/s, a hop under ~2px) isn't a hop any more: the coin lies down
    // The low-resolution silhouette used for the rim follows exactly the
    // same projection as the detailed face. The face keeps all 64 source rows.
    var REST_RIM = Math.round(THICK * Math.cos(VIEW));                       // side wall showing under a coin that lies flat (2px)
    var MAX_ROCK = 0.24;                                                     // maximum settling tilt, in radians
    var rowCache = {};
    function faceRows(h) {
        if (rowCache[h]) return rowCache[h];
        var rows = [], r;
        for (r = 0; r < h; r++) rows.push(Math.min(SIZE - 1, Math.floor((r + 0.5) * SIZE / h)));
        return (rowCache[h] = rows);
    }

    // dark rink ice under a single light: tones from the page background up to lit ice
    var ICE = ['#080d15', '#0a1220', '#0d182a', '#112036', '#162a45', '#1c3656', '#244469'];
    var BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

    function rng(seed) {
        var a = seed >>> 0;
        return function () { a |= 0; a = a + 0x6D2B79F5 | 0; var t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
    }
    function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

    function mount(container, opts) {
        opts = opts || {};
        var Coin = window.ArcadeCoin;
        if (!Coin) throw new Error('arcade-coin.js must be loaded before arcade-coin-toss.js');
        var sound = opts.sound || null, effects = opts.effects !== false;     // effects: sparks + rings on impact
        var density = opts.density || 88;                                      // roughly how many art pixels wide the scene is (more = smaller coins, more ice)
        var inset = opts.inset || 0;                                           // keep resting coins this fraction of the width away from the sides (for a stage whose edges fade out)
        var rand = rng(opts.seed != null ? opts.seed : (Date.now() ^ (Math.random() * 1e9)));
        var R = function (a, b) { return a + rand() * (b - a); };

        var canvas = document.createElement('canvas');
        canvas.className = 'coin-toss-canvas';
        canvas.setAttribute('aria-hidden', 'true');
        canvas.style.cssText = 'display:block;position:absolute;left:50%;top:50%;image-rendering:pixelated;image-rendering:crisp-edges;image-rendering:pixelated;';
        if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
        container.appendChild(canvas);
        var ctx = canvas.getContext('2d');
        var ground = document.createElement('canvas'), gctx = ground.getContext('2d');
        var W = 0, H = 0, S = 4;                                   // art-pixel size of the scene, and CSS px per art pixel

        var coins = [], sparks = [], rings = [], motes = [];
        var clock = 0, acc = 0, landed = 0, settledFired = true, reduced = !!opts.reduced;

        // ── the ice ──
        function paintGround() {
            ground.width = W; ground.height = H;
            var cx = W / 2, cy = H * 0.63, rx = W * 0.5, ry = H * 0.44, x, y;
            var img = gctx.createImageData(W, H), tones = ICE.map(function (h) { return [parseInt(h.substr(1, 2), 16), parseInt(h.substr(3, 2), 16), parseInt(h.substr(5, 2), 16)]; });
            for (y = 0; y < H; y++) for (x = 0; x < W; x++) {
                var dx = (x - cx) / rx, dy = (y - cy) / ry, d = Math.sqrt(dx * dx + dy * dy);
                var light = clamp(1 - d, 0, 1); light = light * light * (3 - 2 * light);                 // smooth pool of light, dark at the rim
                light *= 0.62 + 0.38 * clamp(y / H, 0, 1);                                               // the far ice falls off into the dark
                var v = light * (tones.length - 1), lo = Math.floor(v), t = v - lo;
                var idx = lo + (t > (BAYER[(y & 3) * 4 + (x & 3)] + 0.5) / 16 ? 1 : 0);                   // ordered dither between neighbouring tones
                var c = tones[clamp(idx, 0, tones.length - 1)], o = (y * W + x) * 4;
                img.data[o] = c[0]; img.data[o + 1] = c[1]; img.data[o + 2] = c[2]; img.data[o + 3] = 255;
            }
            gctx.putImageData(img, 0, 0);
            function lit(x, y) { var ax = (x - cx) / rx, ay = (y - cy) / ry; return clamp(1 - Math.sqrt(ax * ax + ay * ay), 0, 1); }
            function dab(x, y, dark, bright) {                                       // paint one pixel of rink marking, as bright as the light on it
                if (x < 0 || y < 0 || x >= W || y >= H) return;
                var l = lit(x, y) * 1.25 + 0.12;
                gctx.fillStyle = l > (BAYER[(y & 3) * 4 + (x & 3)] + 0.5) / 16 ? bright : dark;
                gctx.fillRect(x, y, 1, 1);
            }
            // the blue line, away in the distance
            var by = Math.round(H * 0.17);
            for (x = 0; x < W; x++) { dab(x, by, '#0f2a57', '#1d4f9c'); dab(x, by + 1, '#0d2449', '#173f80'); }
            // the face-off circle the coins are tossed into, and its dot
            var crx = Math.min(W * 0.44, 46), cry = crx * 0.4, steps = Math.ceil(crx * 8);
            for (var st = 0; st < steps; st++) {
                var an = st / steps * TAU, ex = cx + Math.cos(an) * crx, ey = cy + Math.sin(an) * cry;
                dab(Math.round(ex), Math.round(ey), '#4a1420', '#a02c3a');
                if (Math.sin(an) > -0.2) dab(Math.round(ex), Math.round(ey) + 1, '#3a0f19', '#7f2230');       // the near half of the ring is a touch thicker
            }
            for (y = -2; y <= 2; y++) for (x = -5; x <= 5; x++) if ((x * x) / 30 + (y * y) / 5.2 <= 1) dab(Math.round(cx) + x, Math.round(cy) + y, '#5a1824', '#b8323f');
            // skate scratches: short shallow arcs, only where the light reaches
            var sr = rng(7);
            for (var i = 0; i < 17; i++) {
                var sx = cx + (sr() - 0.5) * W * 0.8, sy = cy + (sr() - 0.5) * H * 0.62, len = 6 + sr() * 16, bend = (sr() - 0.5) * 0.5, slope = (sr() - 0.5) * 0.7;
                gctx.fillStyle = sr() < 0.5 ? '#22425f' : '#1c3651';
                for (var k = 0; k < len; k++) {
                    var px = Math.round(sx + k), py = Math.round(sy + k * slope + bend * k * k * 0.08);
                    var ddx = (px - cx) / rx, ddy = (py - cy) / ry;
                    if (ddx * ddx + ddy * ddy < 0.55) gctx.fillRect(px, py, 1, 1);
                }
            }
        }

        function resize() {
            var cw = Math.max(40, container.clientWidth), ch = Math.max(40, container.clientHeight);
            S = clamp(Math.round(cw / density), 3, 8);
            var w = Math.ceil(cw / S), h = Math.ceil(ch / S);
            if (w === W && h === H && canvas.style.width) return;
            var sxk = W ? w / W : 1, syk = H ? h / H : 1;
            W = w; H = h;
            canvas.width = W * DETAIL; canvas.height = H * DETAIL;
            canvas.style.width = W * S + 'px'; canvas.style.height = H * S + 'px';
            canvas.style.marginLeft = -(W * S) / 2 + 'px'; canvas.style.marginTop = -(H * S) / 2 + 'px';
            paintGround();
            coins.forEach(function (c) { c.x *= sxk; c.y *= syk; c.tx *= sxk; c.ty *= syk; });
            motes.length = 0;
            for (var i = 0; i < 16; i++) motes.push({ x: R(0.18, 0.82) * W, y: R(0.12, 0.8) * H, vx: R(-1.2, 1.2), vy: R(-1.6, -0.3), ph: R(0, TAU), sp: R(0.6, 1.8) });
            draw();
        }

        // ── one coin ──
        /**
         * One step of a coin in the air — used by the real coin AND by the planner, so they can never disagree.
         * c: { z, vz, a, spin, hops, rest[], kicks[] }. Returns 0 = flying, 1 = bounced, 2 = its last impact (it lies down); c.hit = impact speed.
         */
        function flyStep(c, dt) {
            c.vz -= G * dt; c.z += c.vz * dt;
            if (c.hops > 0) c.spin *= Math.exp(-dt * (c.hops > 1 ? 6 : 2.2));          // each hop it turns less
            c.a += c.spin * dt;
            if (c.hops > 0) {
                // Close to the ice a coin cannot be steep — its rim would already be touching. So the low hops are nearly flat
                // ones, and the last impact is a flat slap, not a coin landing on its edge and toppling over.
                var flat = Math.round(c.a / Math.PI) * Math.PI, dev = c.a - flat, most = Math.asin(clamp((c.z + 1.5) / (SIZE / 2), 0, 1));
                if (Math.abs(dev) > most) {
                    c.a -= (dev > 0 ? 1 : -1) * (Math.abs(dev) - most) * (1 - Math.exp(-dt * 30));
                    if (c.spin * dev > 0) c.spin *= -0.35;                                   // the rim clips the ice and kicks it back
                }
            }
            if (c.z <= 0 && c.vz < 0) {
                c.z = 0; c.hit = -c.vz;
                c.vz = c.hit * c.rest[Math.min(c.hops, c.rest.length - 1)];
                var kick = c.kicks[Math.min(c.hops, c.kicks.length - 1)];
                c.spin = c.spin * 0.55 * kick[0] + kick[1];
                c.hops++;
                return c.vz < LAST_HOP ? 2 : 1;
            }
            return 0;
        }
        /** Fly a coin ahead of time: how far it travels per unit of starting ground speed (to aim it), and which way up it ends. */
        function flightPlan(z0, vz0, rest, spin0, kicks, a0) {
            var c = { z: z0, vz: vz0, a: a0, spin: spin0, hops: 0, rest: rest, kicks: kicks }, v = 1, d = 0, guard = 0, hit = 0;
            while (guard++ < 6000 && hit !== 2) { hit = flyStep(c, STEP); d += v * STEP; if (hit) v *= 0.62; }
            return { travel: d + v / 4.2, heads: Math.round(c.a / Math.PI) % 2 === 0 };        // + the slide (∫ v·e^(−4.2t) dt)
        }

        function impact(c, last) {
            var first = c.hops === 1, strength = clamp(c.hit / 380, 0, 1);
            c.vx *= 0.62; c.vy *= 0.62;
            if (sound) sound.hit(strength, (c.x / W) * 2 - 1, c.pitch);
            if (effects) rings.push({ x: c.x, y: c.y, t: 0, life: 0.34 + strength * 0.2, r: 5 + strength * 9 });
            var n = effects ? Math.round(2 + strength * 6) : 0;
            for (var i = 0; i < n; i++) {
                var an = R(0, TAU), v = R(18, 70) * (0.4 + strength);
                sparks.push({ x: c.x, y: c.y, z: 1, vx: Math.cos(an) * v, vy: Math.sin(an) * v * 0.5, vz: R(40, 150) * (0.4 + strength), t: 0, life: R(0.25, 0.55), hot: rand() < 0.6 });
            }
            if (first) {
                landed++; if (opts.onLand) opts.onLand(c.i, landed);
                if (navigator.vibrate && (!navigator.userActivation || navigator.userActivation.hasBeenActive)) { try { navigator.vibrate(8); } catch (e) { /* not allowed */ } }
            }
            if (!last) return;
            // It lies down — and rocks on its rim for a moment, the high point running round the edge (a spun coin on a table).
            // A SMALL tilt that dies quickly: at this pixel size a big rock reads as the coin blinking.
            c.z = 0; c.vz = 0; c.phase = 'wobble'; c.wt = 0;
            var flat = Math.round(c.a / Math.PI) * Math.PI, landedTilt = clamp(c.a - flat, -MAX_ROCK, MAX_ROCK);
            c.a = flat;
            c.wob = clamp(0.17 + c.hit / 1500, Math.max(0.18, Math.abs(landedTilt)), MAX_ROCK);      // how far it rocks (radians)…
            c.wth = Math.asin(clamp(landedTilt / c.wob, -1, 1));                                     // …starting exactly from the tilt it landed with: one continuous motion
            c.wdir = rand() < 0.5 ? 1 : -1; c.turns = 0; c.tick = 0;
            c.tilt = c.wob * Math.sin(c.wth); c.roll = c.wob * Math.cos(c.wth);
        }
        function stepCoin(c, dt) {
            if (c.delay > 0) { c.delay -= dt; if (c.delay <= 0 && sound) sound.whoosh((c.tx / W) * 2 - 1); return; }
            if (c.phase === 'air') {
                c.x += c.vx * dt; c.y += c.vy * dt;
                var hit = flyStep(c, dt);
                if (hit) impact(c, hit === 2);
            } else if (c.phase === 'wobble') {
                c.wt += dt;
                var e = Math.exp(-c.wt * 2.1), phi = c.wob * e;          // the tilt dies away…
                c.x += c.vx * dt; c.y += c.vy * dt; c.vx *= Math.exp(-dt * 4.2); c.vy *= Math.exp(-dt * 4.2);      // …while it slides a little: ice
                var turn = TAU * (2.2 + 2 * (1 - e)) * dt;              // …and the rock quickens a little as it dies: 2.2 → ~4 a second. Never faster —
                c.wth += c.wdir * turn; c.turns += turn;                 //    at pixel size anything quicker stops being motion and becomes flicker.
                c.tilt = phi * Math.sin(c.wth);                          // far edge up (+) / near edge up (−)
                c.roll = phi * Math.cos(c.wth);                          // right edge up (+) / left edge up (−)
                var beat = Math.floor(c.turns / Math.PI);
                if (beat !== c.tick) { c.tick = beat; if (sound && e > 0.3) sound.hit(0.05 + 0.2 * e, (c.x / W) * 2 - 1, c.pitch * 1.06, true); }
                // under a pixel of lift there is nothing left to see: stop cleanly instead of shimmering
                if (SIZE * Math.sin(phi) * Math.cos(VIEW) < 1 && Math.abs(c.vx) + Math.abs(c.vy) < 1.5) {
                    c.phase = 'rest'; c.tilt = 0; c.roll = 0; c.x = Math.round(c.x); c.y = Math.round(c.y); c.glintAt = clock + R(0.5, 2.2);
                }
            }
        }

        function toss(count, o) {
            o = o || {};
            var keep = !!o.keep && coins.length > 0;                 // keep: throw these in ON TOP of the coins already lying there
            count = clamp(Math.round(count) || 0, 0, keep ? Math.max(0, 16 - coins.length) : 12);
            if (!keep) { coins = []; sparks = []; rings = []; landed = 0; }
            acc = 0; settledFired = count === 0 && !coins.some(function (c) { return c.phase !== 'rest'; });
            if (!W) resize();
            var base = coins.length, all = base + count;
            var cx = W / 2, cy = H * 0.63 + 2, spots = coins.map(function (c) { return [c.tx, c.ty]; });
            var sprx = Math.min(W * (0.4 - inset * 0.5), 9 + all * 6.5), spry = Math.min(H * 0.16, 5 + all * 2.6);
            for (var i = 0; i < count; i++) {
                var tx = cx, ty = cy, tries = 0, ok = false;
                while (!ok && tries++ < 60) {
                    var an = R(0, TAU), rr = Math.sqrt(rand()) * (tries > 40 ? 1.5 : 1);
                    tx = cx + Math.cos(an) * sprx * rr; ty = cy + Math.sin(an) * spry * rr;
                    ok = tx > Math.max(11, W * inset) && tx < W - Math.max(11, W * inset) && spots.every(function (p) { var dx = p[0] - tx, dy = (p[1] - ty) * 1.9; return dx * dx + dy * dy > 19 * 19; });
                }
                spots.push([tx, ty]);
                var z0 = R(76, 104), vz0 = R(-30, 40), rest = [R(0.46, 0.56), R(0.4, 0.52), R(0.34, 0.46)], kicks = [];
                for (var kk = 0; kk < 6; kk++) kicks.push([rand() < 0.22 ? -1 : 1, R(-2, 2)]);
                var sx = cx + (tx - cx) * 0.35 + R(-10, 10), sy = H * R(0.08, 0.2);                                         // thrown from the far side, toward you
                var spin = R(13, 21) * (rand() < 0.5 ? -1 : 1), a0 = R(0, TAU), plan = flightPlan(z0, vz0, rest, spin, kicks, a0);
                // The coin's face is the puck, so the first one down always shows it; the rest fall as they fall.
                for (var tryN = 0; i === 0 && !keep && !plan.heads && tryN < 16; tryN++) { a0 = R(0, TAU); plan = flightPlan(z0, vz0, rest, spin, kicks, a0); }
                coins.push({
                    i: base + i, x: sx, y: sy, z: z0, vx: (tx - sx) / plan.travel, vy: (ty - sy) / plan.travel, vz: vz0, tx: tx, ty: ty, rest: rest, kicks: kicks,
                    a: a0, spin: spin, tilt: 0, roll: 0, hops: 0, phase: 'air', delay: 0.05 + i * R(0.09, 0.15) + (i ? R(0, 0.05) : 0),
                    pitch: R(0.94, 1.07), glintAt: 0, side: null
                });
            }
            if (o.instant || reduced) finish();
            draw();
        }

        function finish() {
            coins.forEach(function (c, n) {
                if (c.phase === 'rest') return;
                if (c.hops === 0) { landed++; if (opts.onLand) opts.onLand(c.i, landed); }
                c.x = Math.round(c.tx); c.y = Math.round(c.ty); c.z = 0; c.delay = 0; c.tilt = 0; c.roll = 0; c.phase = 'rest';
                c.a = (n === 0 || rand() < 0.62 ? 0 : Math.PI); c.glintAt = clock + R(0.4, 2);
            });
            sparks = []; rings = [];
            settleCheck();
            draw();
        }
        function settleCheck() {
            if (settledFired || coins.some(function (c) { return c.phase !== 'rest'; })) return;
            settledFired = true;
            if (sound) sound.chime(coins.length);
            if (opts.onSettle) opts.onSettle();
        }

        // ── drawing ──
        function ellipseRows(cx, cy, rx, ry, fn) {
            for (var y = -Math.ceil(ry); y <= Math.ceil(ry); y++) {
                var k = 1 - (y * y) / (ry * ry || 1);
                if (k < 0) continue;
                var half = Math.round(rx * Math.sqrt(k));
                fn(Math.round(cx) - half, Math.round(cy) + y, half * 2);
            }
        }
        var scratch = document.createElement('canvas'); scratch.width = scratch.height = SIZE * DETAIL;
        var sctx = scratch.getContext('2d');
        function drawCoin(c) {
            var ang = c.a + c.tilt, view = Math.sin(ang + VIEW), side = view >= 0 ? 'heads' : 'tails', f = Math.abs(view);
            var lift = Math.cos(ang + VIEW) * (view >= 0 ? 1 : -1);                    // > 0: the rim shows below the face
            var x = Math.round(c.x - SIZE / 2), moving = c.phase !== 'rest', flying = c.phase === 'air';
            var h, rim, faceTop, sideUp = 0, r;
            if (flying) {                                                               // tumbling: the face is as tall as the angle says, sitting on its lowest point
                h = Math.max(1, Math.round(SIZE * f)); rim = Math.round(THICK * Math.abs(lift));
                var dev = ang - Math.round(ang / Math.PI) * Math.PI;                   // tilt from the nearest flat; < 0 = near edge up
                var low = Math.round(c.y - c.z - (dev < 0 ? SIZE * Math.sin(-dev) * Math.cos(VIEW) : 0));
                if (f < 0.13) {                                                         // edge-on: just the milled rim
                    var ey = low - THICK;
                    ctx.fillStyle = Coin.PAL.o; ctx.fillRect(x + 1, ey - 1, SIZE - 2, THICK + 2); ctx.fillRect(x, ey, SIZE, THICK);
                    ctx.fillStyle = Coin.PAL.m; ctx.fillRect(x + 1, ey, SIZE - 2, THICK);
                    ctx.fillStyle = Coin.PAL.h; ctx.fillRect(x + 2, ey, SIZE - 5, 1);
                    ctx.fillStyle = Coin.PAL.d; ctx.fillRect(x + 1, ey + THICK - 1, SIZE - 2, 1);
                    c.side = side;
                    return;
                }
                faceTop = lift > 0 ? low - h - rim : low - h;
            } else {
                // On the ice (rocking, or still). The coin pivots on whichever part of its rim is down: tipping toward us lifts the
                // FAR edge (the top rises), tipping away lifts the NEAR edge (the bottom comes off the ice, the shadow stays put),
                // tipping sideways lifts one side (a shear, column by column). Each edge is worked out as a smooth number, relative
                // to the coin, and rounded ONCE — stacking rounded pieces (or rounding against a sliding position) is what makes a
                // settling sprite shimmer.
                var nearUp = c.tilt < 0 ? SIZE * Math.sin(Math.min(-c.tilt, Math.PI / 2)) * Math.cos(VIEW) : 0;
                sideUp = (SIZE / 2) * Math.sin(c.roll) * Math.cos(VIEW);             // ± px at the outer columns; the middle rides up by |sideUp|
                rim = REST_RIM; lift = 1;
                var baseY = Math.round(c.y), bottomRel = -rim - nearUp - Math.abs(sideUp);
                faceTop = baseY + Math.round(bottomRel - SIZE * f);
                h = clamp(baseY + Math.round(bottomRel) - faceTop, 1, SIZE);
            }
            var tone = !flying ? 'normal' : f > 0.94 ? 'bright' : f < 0.42 ? 'dim' : 'normal';
            var sprite = Coin.canvas(side, tone, 64), srcRows = faceRows(h), mask = Coin.pixels(side, 16);
            var shift = function (col) { return sideUp ? -Math.round(sideUp * (col - 7.5) / 7.5) : 0; };
            c.side = side;
            // the side wall: under (or over) every column of the face, the coin's thickness — a crescent hugging the edge turned away from us
            if (rim > 0) for (var col = 0; col < SIZE; col++) {
                var edgeRow = -1;
                if (lift > 0) { for (r = h - 1; r >= 0; r--) if (mask[srcRows[r]][col] !== '.') { edgeRow = r; break; } }
                else { for (r = 0; r < h; r++) if (mask[srcRows[r]][col] !== '.') { edgeRow = r; break; } }
                if (edgeRow < 0) continue;
                for (var t = 1; t <= rim; t++) {
                    ctx.fillStyle = t === rim ? Coin.PAL.o : (col < 6 ? Coin.PAL.m : Coin.PAL.d);
                    ctx.fillRect(x + col, faceTop + edgeRow + (lift > 0 ? t : -t) + shift(col), 1, 1);
                }
            }
            if (!sideUp) ctx.drawImage(sprite, x, faceTop, SIZE, h);
            else {                                                                      // preserve the engraving when the face tips sideways
                sctx.clearRect(0, 0, SIZE * DETAIL, SIZE * DETAIL);sctx.imageSmoothingEnabled=false;
                sctx.drawImage(sprite, 0, 0, SIZE * DETAIL, h * DETAIL);
                for (var cc = 0; cc < SIZE * DETAIL; cc++) {
                    var offset = -Math.round(sideUp * (cc / DETAIL - 7.5) / 7.5 * DETAIL) / DETAIL;
                    ctx.drawImage(scratch, cc, 0, 1, h * DETAIL, x + cc / DETAIL, faceTop + offset, 1 / DETAIL, h);
                }
            }
            if (!moving && clock > c.glintAt) {                                         // lying still, it catches the light now and then
                var p = (clock - c.glintAt) / 0.5;
                if (p > 1) c.glintAt = clock + R(2.4, 5);
                else {
                    var sweep = Math.round(-6 + p * 28);
                    ctx.fillStyle = 'rgba(255,250,232,0.85)';
                    for (r = 0; r < h; r++) for (var q = 0; q < 2; q++) { var gx = sweep - r + q; if (gx > 0 && gx < SIZE - 1 && parseInt(mask[srcRows[r]][gx], 16) >= 6) ctx.fillRect(x + gx, faceTop + r, 1, 1); }
                }
            }
        }
        function drawUnder(c) {                                                         // shadow + a faint reflection in the ice
            if (c.delay > 0) return;
            // A coin lying on the ice only shows a sliver of contact shadow hugging its near edge; in the air the shadow
            // drops away beneath it, smaller and fainter the higher it is.
            var k = clamp(1 - c.z / 110, 0.15, 1), rx = 3 + 5.2 * k, ry = 1 + 1.5 * k;
            ctx.fillStyle = 'rgba(0,0,0,' + (0.2 + 0.36 * k).toFixed(3) + ')';
            ellipseRows(c.x + 1 + c.z * 0.06, c.y - 2 + Math.min(3, c.z * 0.4), rx, ry, function (x, y, w) { ctx.fillRect(x, y, w, 1); });      // on the ice it hides under the coin; it slips out as the coin lifts
            if (c.z < 14) {                                                             // polished ice: a breath of gold under it
                var fade = 1 - c.z / 14, left = Math.round(c.x - SIZE / 2) + 3, yy = Math.round(c.y) + 1;
                [0.17, 0.1, 0.05].forEach(function (al, n) { ctx.fillStyle = 'rgba(240,179,35,' + (al * fade).toFixed(3) + ')'; ctx.fillRect(left + n, yy + n, SIZE - 6 - n * 2, 1); });
            }
        }
        function draw() {
            if (!W) return;
            ctx.setTransform(DETAIL, 0, 0, DETAIL, 0, 0);
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(ground, 0, 0);
            var i, m;
            rings.forEach(function (g) {                                                // the ice rings where a coin strikes it
                var p = g.t / g.life, rx = 3 + g.r * p, ry = rx * 0.42;
                ctx.fillStyle = 'rgba(190,225,255,' + (0.5 * (1 - p)).toFixed(3) + ')';
                for (var a = 0; a < 28; a++) { var an = a / 28 * TAU; ctx.fillRect(Math.round(g.x + Math.cos(an) * rx), Math.round(g.y + 1 + Math.sin(an) * ry), 1, 1); }
            });
            var order = coins.filter(function (c) { return c.delay <= 0; }).sort(function (a, b) { return a.y - b.y; });
            order.forEach(drawUnder);
            order.forEach(drawCoin);
            sparks.forEach(function (s) {
                var p = s.t / s.life;
                ctx.fillStyle = s.hot ? (p < 0.4 ? '#fffdf0' : p < 0.75 ? '#ffd75e' : '#c98d14') : (p < 0.6 ? '#dff1ff' : '#7fa6c9');
                ctx.fillRect(Math.round(s.x), Math.round(s.y - s.z), 1, 1);
            });
            for (i = 0; effects && i < motes.length; i++) {                             // dust hanging in the light
                m = motes[i];
                var tw = 0.5 + 0.5 * Math.sin(m.ph + clock * m.sp);
                if (tw < 0.35) continue;
                ctx.fillStyle = 'rgba(190,220,255,' + (0.1 + 0.22 * tw).toFixed(3) + ')';
                ctx.fillRect(Math.round(m.x), Math.round(m.y), 1, 1);
            }
        }

        function advance(dt) {
            dt = clamp(dt || 0, 0, 0.1);
            clock += dt; acc += dt;
            while (acc >= STEP) {
                acc -= STEP;
                for (var i = 0; i < coins.length; i++) stepCoin(coins[i], STEP);
            }
            var k;
            for (k = sparks.length - 1; k >= 0; k--) {
                var s = sparks[k]; s.t += dt;
                s.vz -= G * 0.5 * dt; s.z += s.vz * dt; s.x += s.vx * dt; s.y += s.vy * dt;
                if (s.z < 0) { s.z = 0; s.vz = 0; s.vx *= 0.8; s.vy *= 0.8; }
                if (s.t >= s.life) sparks.splice(k, 1);
            }
            for (k = rings.length - 1; k >= 0; k--) { rings[k].t += dt; if (rings[k].t >= rings[k].life) rings.splice(k, 1); }
            for (k = 0; k < motes.length; k++) {
                var m = motes[k]; m.x += m.vx * dt; m.y += m.vy * dt;
                if (m.y < H * 0.08) { m.y = H * 0.82; m.x = R(0.18, 0.82) * W; }
                if (m.x < W * 0.1 || m.x > W * 0.9) m.vx *= -1;
            }
            settleCheck();
            draw();
        }

        var ro = window.ResizeObserver ? new ResizeObserver(function () { resize(); }) : null;
        if (ro) ro.observe(container);
        resize();

        return {
            canvas: canvas, toss: toss, advance: advance, finish: finish, resize: resize, draw: draw,
            setReduced: function (v) { reduced = !!v; },
            get busy() { return coins.some(function (c) { return c.phase !== 'rest'; }); },
            get landed() { return landed; },
            get scale() { return S; },
            state: function () { return { w: W, h: H, scale: S, landed: landed, coins: coins.map(function (c) { return { x: Math.round(c.x), y: Math.round(c.y), z: Math.round(c.z), phase: c.phase, side: c.side, tx: Math.round(c.tx), ty: Math.round(c.ty), hops: c.hops, tilt: +(c.tilt || 0).toFixed(3), roll: +(c.roll || 0).toFixed(3) }; }) }; },
            destroy: function () { if (ro) ro.disconnect(); if (canvas.parentNode) canvas.parentNode.removeChild(canvas); coins = []; }
        };
    }

    /**
     * The sound of it, synthesised (no audio files): a coin on hard ice is a click, a few bright inharmonic
     * partials that die in a tenth of a second, and a tiny thud from the surface.
     *   getContext() → AudioContext | null     getOutput() → AudioNode to play into     isOpen() → may we make sound right now?
     */
    function createSynth(getContext, getOutput, isOpen) {
        var PARTIALS = [[4180, 1, 0.16], [6320, 0.62, 0.11], [9150, 0.4, 0.075], [11800, 0.22, 0.05], [2870, 0.3, 0.13]];
        var noiseBuf = null, room = null;
        function ready() { var c = getContext(); return c && c.state === 'running' && (!isOpen || isOpen()) ? c : null; }
        function noise(c) {
            if (noiseBuf && noiseBuf.sampleRate === c.sampleRate) return noiseBuf;
            noiseBuf = c.createBuffer(1, c.sampleRate, c.sampleRate);
            var d = noiseBuf.getChannelData(0);
            for (var i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
            return noiseBuf;
        }
        function out(c, pan) {
            var dest = getOutput();
            if (!c.createStereoPanner) return dest;
            var p = c.createStereoPanner(); p.pan.value = clamp(pan || 0, -1, 1) * 0.6; p.connect(dest);
            return p;
        }
        return {
            /** strength 0..1, pan −1..1, pitch ≈ 1, soft = the rattle as it lies down */
            hit: function (strength, pan, pitch, soft) {
                var c = ready(); if (!c) return;
                var t = c.currentTime, dest = out(c, pan), vol = (soft ? 0.05 : 0.11) * (0.25 + strength * 0.9);
                PARTIALS.forEach(function (p, i) {
                    if (soft && i > 2) return;
                    var o = c.createOscillator(), g = c.createGain(), dur = p[2] * (soft ? 0.55 : 1) * (0.7 + strength * 0.6);
                    o.type = 'sine'; o.frequency.value = p[0] * (pitch || 1) * (1 + (Math.random() - 0.5) * 0.012);
                    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vol * p[1], t + 0.0015); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
                    o.connect(g); g.connect(dest); o.start(t); o.stop(t + dur + 0.02);
                });
                var n = c.createBufferSource(), f = c.createBiquadFilter(), ng = c.createGain();          // the click of contact
                n.buffer = noise(c); f.type = 'bandpass'; f.frequency.value = 5200; f.Q.value = 0.8;
                ng.gain.setValueAtTime(vol * 1.5, t); ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.012);
                n.connect(f); f.connect(ng); ng.connect(dest); n.start(t, Math.random() * 0.5, 0.03);
                if (!soft && strength > 0.25) {                                                             // and the ice answering
                    var th = c.createOscillator(), tg = c.createGain();
                    th.type = 'sine'; th.frequency.setValueAtTime(210, t); th.frequency.exponentialRampToValueAtTime(90, t + 0.06);
                    tg.gain.setValueAtTime(vol * 1.1 * strength, t); tg.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
                    th.connect(tg); tg.connect(dest); th.start(t); th.stop(t + 0.1);
                }
            },
            whoosh: function (pan) {
                var c = ready(); if (!c) return;
                var t = c.currentTime, n = c.createBufferSource(), f = c.createBiquadFilter(), g = c.createGain();
                n.buffer = noise(c); f.type = 'bandpass'; f.Q.value = 1.4; f.frequency.setValueAtTime(500, t); f.frequency.exponentialRampToValueAtTime(2600, t + 0.28);
                g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.035, t + 0.08); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);
                n.connect(f); f.connect(g); g.connect(out(c, pan)); n.start(t, Math.random() * 0.4, 0.4);
            },
            chime: function (count) {
                var c = ready(); if (!c || !count) return;
                var t = c.currentTime + 0.08, dest = getOutput();
                [1318.5, 1975.5].forEach(function (hz, i) {
                    var o = c.createOscillator(), g = c.createGain(), at = t + i * 0.11;
                    o.type = 'sine'; o.frequency.value = hz;
                    g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(0.05, at + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, at + 0.7);
                    o.connect(g); g.connect(dest); o.start(at); o.stop(at + 0.75);
                });
            },
            /** the empty-rink hush under it all: on() fades it in, off() fades it out */
            ambience: function (on) {
                var c = getContext();
                if (!on) {
                    if (!room) return;
                    var r = room; room = null;
                    try { r.gain.gain.cancelScheduledValues(c.currentTime); r.gain.gain.setTargetAtTime(0, c.currentTime, 0.25); r.src.stop(c.currentTime + 1.2); } catch (e) { /* already gone */ }
                    return;
                }
                c = ready(); if (!c || room) return;
                var src = c.createBufferSource(), lo = c.createBiquadFilter(), hum = c.createBiquadFilter(), g = c.createGain(), mix = c.createGain();
                src.buffer = noise(c); src.loop = true;
                lo.type = 'lowpass'; lo.frequency.value = 420; lo.Q.value = 0.4;
                hum.type = 'bandpass'; hum.frequency.value = 118; hum.Q.value = 5;                          // the compressors, somewhere in the building
                src.connect(lo); lo.connect(mix); src.connect(hum); hum.connect(mix); mix.connect(g); g.connect(getOutput());
                g.gain.setValueAtTime(0, c.currentTime); g.gain.setTargetAtTime(0.05, c.currentTime, 0.6);
                src.start();
                room = { src: src, gain: g };
            }
        };
    }

    window.ArcadeCoinToss = { mount: mount, createSynth: createSynth };
})();
